import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { UserSort } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// The platform's accounts, read-only. Spec: docs/superpowers/specs/2026-10-15-admin-utenti-design.md

// Each filter has a word for "no filter", and the type's default is customers, not everyone:
// staff who use the app as parents would otherwise crowd the list (spec §3.2).
const USER_TYPES = ['customer', 'staff', 'all'] as const
const USER_PLAN_FILTERS = ['all', 'free', 'covered'] as const
const USER_PURCHASE_FILTERS = ['all', 'with', 'without'] as const

type UserTypeFilter = (typeof USER_TYPES)[number]
type UserPlanFilter = (typeof USER_PLAN_FILTERS)[number]
type UserPurchaseFilter = (typeof USER_PURCHASE_FILTERS)[number]

export interface UserSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly type: UserTypeFilter
  readonly plan: UserPlanFilter
  readonly purchases: UserPurchaseFilter
  readonly sort: UserSort
}

/** The orderings the screen offers. `last_seen_at` ascending is left out: nulls go last both
 * ways, so it would read as "least recent first" only after every account seen at all. */
const USER_SORTS: readonly UserSort[] = ['-created_at', 'created_at', '-last_seen_at']

export const DEFAULT_USER_SEARCH: UserSearch = {
  page: 1,
  q: null,
  type: 'customer',
  plan: 'all',
  purchases: 'all',
  sort: '-created_at',
}

export function parseUserType(value: unknown): UserTypeFilter {
  return parseOneOf(USER_TYPES, value) ?? DEFAULT_USER_SEARCH.type
}

export function parsePlanFilter(value: unknown): UserPlanFilter {
  return parseOneOf(USER_PLAN_FILTERS, value) ?? DEFAULT_USER_SEARCH.plan
}

export function parsePurchaseFilter(value: unknown): UserPurchaseFilter {
  return parseOneOf(USER_PURCHASE_FILTERS, value) ?? DEFAULT_USER_SEARCH.purchases
}

function parseUserSort(value: unknown): UserSort {
  return parseOneOf(USER_SORTS, value) ?? DEFAULT_USER_SEARCH.sort
}

/** Customers is the starting view, not a filter the operator chose, so it does not count. */
export function hasFilters(search: UserSearch): boolean {
  return (
    search.q !== null ||
    search.type !== DEFAULT_USER_SEARCH.type ||
    search.plan !== DEFAULT_USER_SEARCH.plan ||
    search.purchases !== DEFAULT_USER_SEARCH.purchases
  )
}

export function withoutFilters(): Partial<UserSearch> {
  const { type, plan, purchases } = DEFAULT_USER_SEARCH
  return { q: null, type, plan, purchases }
}

/** The list as a link target: path and the search params its type requires, in one place. */
export const userListLink = { to: '/users', search: DEFAULT_USER_SEARCH } as const

export const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: (search: Record<string, unknown>): UserSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    type: parseUserType(search.type),
    plan: parsePlanFilter(search.plan),
    purchases: parsePurchaseFilter(search.purchases),
    sort: parseUserSort(search.sort),
  }),
  search: {
    // Defaults leave the URL: the first load is a bare /users, customers included.
    middlewares: [stripSearchParams(DEFAULT_USER_SEARCH)],
  },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/users.tsx')),
})
